import { refreshAuthTokens } from "@/taskpane/services/auth";
import { Tokens } from "@/taskpane/types/auth";
import axios from "axios";
import { clearSenstiveDataFromLocalStorage } from "@/taskpane/utils/storage";

export const apiClient = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL_API_ADDIN,
});

export const TOKEN_STORAGE_KEY = "loggedInUserTokensByByLaw";

apiClient.interceptors.request.use(async (config) => {
  // // DEBUG LOGGING: Log every API request (commented out for production)
  // const fullUrl = `${config.baseURL}${config.url}`;
  // console.log('🌐 [apiClient] REQUEST:', {
  //   method: config.method?.toUpperCase(),
  //   url: config.url,
  //   baseURL: config.baseURL,
  //   fullUrl: fullUrl,
  //   hasData: !!config.data,
  // });
  
  try {
    const storedTokens = await getAuthTokens();
    if (storedTokens) {
      config.headers.Authorization = `Bearer ${storedTokens.IdToken}`;
      // console.log('[apiClient] Adding auth header to request:', config.url, 'Token present:', !!storedTokens.IdToken);
    } else {
      // console.log('[apiClient] No stored tokens found for request:', config.url);
    }
  } catch (error) {
    console.error('[apiClient] Error in request interceptor:', error);
    clearSenstiveDataFromLocalStorage();
  }
  return config;
});

export const cognitoClient = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL_COGNITO,
});

/**
 * Saves the authentication tokens to the local storage.
 * @param tokensData - The authentication tokens data to be saved.
 * @returns The saved authentication tokens.
 */
export function saveAuthTokens(tokensData: Omit<Tokens, "ExpiresOn">) {
  // console.log('[saveAuthTokens] Saving tokens to localStorage...');
  const tokens: Tokens = {
    ...tokensData,
    ExpiresOn: new Date(Date.now() + tokensData.ExpiresIn * 1000).toISOString(),
  };
  // console.log('[saveAuthTokens] Token will expire on:', tokens.ExpiresOn);
  localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokens));
  // console.log('[saveAuthTokens] Tokens saved successfully');
  return tokens;
}

/**
 * Retrieves the authentication tokens from local storage.
 *
 * @returns A promise that resolves to the authentication tokens or null if no tokens are found.
 */
export async function getAuthTokens(): Promise<Tokens | null> {
  // console.log('[getAuthTokens] Retrieving tokens from localStorage...');
  const storedValue = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (!storedValue) {
    // console.log('[getAuthTokens] No stored tokens found');
    return null;
  }
  
  // console.log('[getAuthTokens] Found stored tokens, parsing...');
  const tokens: Tokens = JSON.parse(storedValue);
  
  try {
    const expiresOn = new Date(tokens.ExpiresOn);
    // console.log('[getAuthTokens] Token expires on:', expiresOn.toISOString());
    // console.log('[getAuthTokens] Current time:', new Date().toISOString());
    
    // refresh token 5 minutes before expiration
    if (expiresOn.getTime() < Date.now() + 300_000) {
      // console.log('[getAuthTokens] Token expires soon, checking if refresh is possible...');
      
      // Only attempt refresh if we have a refresh token
      if (tokens.RefreshToken && tokens.RefreshToken.length > 0) {
        // console.log('[getAuthTokens] Refresh token available, attempting refresh...');
        const t = await refreshAuthTokens(tokens.RefreshToken);
        const refreshedTokens = saveAuthTokens({ ...t, RefreshToken: tokens.RefreshToken });
        // console.log('[getAuthTokens] Token refreshed successfully');
        return refreshedTokens;
      } else {
        // console.log('[getAuthTokens] No refresh token available, returning current tokens');
        // If token is expired and no refresh token, clear it
        if (expiresOn.getTime() < Date.now()) {
          // console.log('[getAuthTokens] Token is expired and no refresh token, clearing tokens');
          localStorage.removeItem(TOKEN_STORAGE_KEY);
          return null;
        }
      }
    }
  } catch (error) {
    console.error('[getAuthTokens] Error during token refresh:', error);
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    return null;
  }

  // console.log('[getAuthTokens] Returning valid tokens');
  return tokens;
}
